import { ChangeEvent, FC, MouseEvent, useMemo, useState } from "react";
import isEqual from "react-fast-compare";
import { Recipe, RecipeDetail } from "../../../services/recipe/recipe.types";
import { useUpdateRecipe } from "../../../services/recipe/recipe.service";
import { useGenericErrorMessage } from "../../../utils/useGenericErrorMessage/useGenericErrorMessage";
import { InputWrapperLarge, InputWrapperMedium } from "../../../common/FormElementWrappers/FormElementWrappers.styles";
import Input from "../../../components/Input/Input";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { statusOptions } from "../../Bases/EditBase/StatusOptions";
import { EditForm, EditFormActions, EditFormFields, FormFieldRow } from "../../Bases/EditForm.styles";
import Button from "../../../components/Button/Button";
import { useGetClasses } from "../../../services/class/class.service";
import { useGetClassBases } from "../../../services/base/base.service";
import { useGetClassSizes } from "../../../services/size/size.service";
import { useGetProducts } from "../../../services/product/product.service";
import { useFetchErrorToast } from "../../../utils/useFetchErrorToasts/useFetchErrorToast";
import UnsavedChangesModal from "../../../common/UnsavedChangesModal/UnsavedChangesModal";
import EditRecipeDetailsTables, { RawProductUsage } from "./EditRecipeDetailsTables";

interface EditRecipeFormProps {
  recipe: Recipe;
  recipeDetails: RecipeDetail[];
  onSuccess: () => void;
}

interface EditRecipeFormValues {
  status: string;
  description: string;
  longDescription: string;
  classId: string;
  baseId: string;
  sizeId: string;
  productId: string;
  fmsProductId: string;
}

const EditRecipeForm: FC<EditRecipeFormProps> = (
    {
      recipe,
      recipeDetails,
      onSuccess
    }
) => {
  const recipeId = recipe.recipeId
  const {
    mutate: updateRecipe,
    isLoading: updateRecipeIsLoading,
    isError: updateRecipeIsError,
    error: updateRecipeError
  } = useUpdateRecipe({ recipeId });

  useGenericErrorMessage([
    {
      isError: updateRecipeIsError,
      error: updateRecipeError
    }
  ])

  const initialFormValues = useMemo<EditRecipeFormValues>(() =>
      ({
        status: recipe.status?.toString() ?? '',
        description: recipe.description,
        longDescription: recipe.longDescription,
        classId: recipe.classId?.toString() ?? '',
        baseId: recipe.baseId?.toString() ?? '',
        sizeId: recipe.sizeId?.toString() ?? '',
        productId: recipe.productId?.toString() ?? '',
        fmsProductId: recipe.fmsProductId?.toString() ?? ''
      }),
      [recipe]
  );

  const initialSelectedRawProducts = useMemo<RawProductUsage[]>(() =>
      (
          recipeDetails.map((recipeDetail) => ({
            rawProductId: recipeDetail.rawProduct.moaRawProdNo,
            rawProductDescription: recipeDetail.rawProduct.moaRawProdDesc,
            usageAmount: recipeDetail.usageAmount
          }))
      ),
      [recipeDetails]
  );

  const [formValues, setFormValues] = useState<EditRecipeFormValues>(initialFormValues);
  const [selectedRawProducts, setSelectedRawProducts] = useState<RawProductUsage[]>(initialSelectedRawProducts);

  const [formErrors, setFormErrors] = useState({
    status: '',
    description: '',
    longDescription: '',
    fmsProductId: ''
  });

  const unsavedChanges =
      !isEqual(initialFormValues, formValues)
      || !isEqual(initialSelectedRawProducts, selectedRawProducts);

  const updateFormValues = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value
    });
  }

  const handleSave = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    let error = false;
    let newFormErrors = {
      status: '',
      description: '',
      longDescription: '',
      fmsProductId: ''
    }

    if (formValues.description.length > 32) {
      error = true;
      newFormErrors.description = 'Description must be 32 characters or less';
    }

    if (formValues.longDescription.length > 255) {
      error = true;
      newFormErrors.longDescription = 'Long description must be 255 characters or less';
    }

    const fmsProductId = formValues.fmsProductId.trim();
    if (fmsProductId === '') {
      error = true;
      newFormErrors.fmsProductId = 'FMS Product ID is required';
    } else if (!/^\d+$/.test(fmsProductId)) {
      error = true;
      newFormErrors.fmsProductId = 'FMS Product ID must be a non-negative whole number';
    }

    if (error) {
      setFormErrors(newFormErrors);
      return;
    }

    updateRecipe(
        {
          status: +formValues.status,
          description: formValues.description,
          longDescription: formValues.longDescription,
          classId: +formValues.classId,
          baseId: +formValues.baseId,
          sizeId: +formValues.sizeId,
          productId: +formValues.productId,
          fmsProductId: +formValues.fmsProductId,
          createRecipeDetailRequests: selectedRawProducts.map((rawProduct) => ({
            rawProductId: rawProduct.rawProductId,
            usageAmount: rawProduct.usageAmount
          }))
        },
        {
          onSuccess: onSuccess
        }
    );
  }

  const {
    data: getClassesResponse,
    isError: getClassesIsError,
    error: getClassesError
  } = useGetClasses(0);
  const classes = getClassesResponse?.data

  const {
    data: getClassBasesResponse,
    isError: getClassBasesIsError,
    error: getClassBasesError
  } = useGetClassBases(
      { classNo: formValues.classId, status: 0 },
      !!formValues.classId
  )
  const classBases = getClassBasesResponse?.data

  const {
    data: getClassSizesResponse,
    isError: getClassSizesIsError,
    error: getClassSizesError
  } = useGetClassSizes(
      { classNo: formValues.classId, status: 0 },
      !!formValues.classId
  )
  const classSizes = getClassSizesResponse?.data

  const {
    data: getProductsResponse,
    isError: getProductsIsError,
    error: getProductsError
  } = useGetProducts(
      { classNo: +formValues.classId, status: 0 },
      !!formValues.classId
  )
  const products = getProductsResponse?.data

  useFetchErrorToast([
    {
      label: 'Error fetching classes',
      id: 'classFetchError',
      isError: getClassesIsError,
      error: getClassesError
    },
    {
      label: 'Error fetching bases',
      id: 'baseFetchError',
      isError: getClassBasesIsError,
      error: getClassBasesError
    },
    {
      label: 'Error fetching sizes',
      id: 'sizeFetchError',
      isError: getClassSizesIsError,
      error: getClassSizesError
    },
    {
      isError: getProductsIsError,
      label: 'Error fetching products',
      id: 'productFetchError',
      error: getProductsError
    }
  ]);

  return (
      <EditForm>
        <EditFormFields>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Recipe ID"}
                  id={'recipeId'}
                  value={recipe.recipeId.toString()}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperLarge>
              <Dropdown
                  label={"Status"}
                  id={'status'}
                  name={'status'}
                  options={statusOptions}
                  value={formValues.status}
                  onChange={updateFormValues}
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperLarge>
              <Input
                  label={"Description"}
                  id={'description'}
                  name={'description'}
                  value={formValues.description}
                  onChange={updateFormValues}
                  errorText={formErrors.description}
              />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Input
                  label={"Long Description"}
                  id={'longDescription'}
                  name={'longDescription'}
                  value={formValues.longDescription}
                  onChange={updateFormValues}
                  errorText={formErrors.longDescription}
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"FMS Product Id"}
                  id={'fmsProductId'}
                  name={'fmsProductId'}
                  value={formValues.fmsProductId}
                  onChange={updateFormValues}
                  errorText={formErrors.fmsProductId}
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <Dropdown
                  label={'Class'}
                  id={'classId'}
                  name={'classId'}
                  options={classes?.map((c) => (
                      { value: c.classNo.toString(), label: `${c.classNo} - ${c.description}` }
                  ))}
                  value={formValues.classId}
                  onChange={updateFormValues}
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Dropdown
                  label={'Base'}
                  id={'baseId'}
                  name={'baseId'}
                  options={classBases?.map((classBase) => (
                      {
                        value: classBase.baseData.baseNo.toString(),
                        label: `${classBase.baseData.baseNo} - ${classBase.baseData.description}`
                      }
                  ))}
                  value={formValues.baseId}
                  onChange={updateFormValues}
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Dropdown
                  label={'Size'}
                  id={'sizeId'}
                  name={'sizeId'}
                  options={classSizes?.map((classSize) => (
                      {
                        value: classSize.sizeData.sizeNo.toString(),
                        label: `${classSize.sizeData.sizeNo} - ${classSize.sizeData.description}`
                      }
                  ))}
                  value={formValues.sizeId}
                  onChange={updateFormValues}
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Dropdown
                  label={'Product'}
                  id={'productId'}
                  name={'productId'}
                  options={products?.map((product) => (
                      {
                        value: product.productNo.toString(),
                        label: `${product.productNo} - ${product.description}`
                      }
                  ))}
                  value={formValues.productId}
                  onChange={updateFormValues}
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <EditRecipeDetailsTables
                selectedRawProducts={selectedRawProducts}
                setSelectedRawProducts={setSelectedRawProducts}
            />
          </FormFieldRow>
        </EditFormFields>
        <EditFormActions>
          <Button
              label={"Save"}
              onClick={handleSave}
              disabled={!unsavedChanges}
              isLoading={updateRecipeIsLoading}
          />
        </EditFormActions>
        <UnsavedChangesModal unsavedChangesExist={unsavedChanges}/>
      </EditForm>
  )
}

export default EditRecipeForm;